// NotificationsPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import { fetchNotifications } from "../Apis/AdminApis";
import "./NotificationsPage.css";

const NotificationsPage = ({ role = "admin" }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine dashboard path based on role
  const dashboardPath = role === "superadmin" ? "/superadmin/dashboard" : "/admin/dashboard";

  useEffect(() => {
    loadAllNotifications();
  }, []);

  const loadAllNotifications = async () => {
    try {
      setIsLoading(true);
      const res = await fetchNotifications("list");
      
      if (res?.error) {
        setError(res.error);
        return;
      }

      setNotifications(Array.isArray(res?.notifications) ? res.notifications : []);
    } catch (err) {
      console.error("Failed to load notifications:", err);
      setError("Failed to load notifications");
    } finally {
      setIsLoading(false);
    }
  };

  const parseBackendDate = (dateString) => {
    if (!dateString) return null;
    const iso = new Date(dateString);
    if (!isNaN(iso)) return iso;
    const match = dateString.match(/(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (match) {
      let [, day, month, year, hour, minute, meridiem] = match;
      hour = parseInt(hour, 10);
      if (meridiem.toUpperCase() === "PM" && hour !== 12) hour += 12;
      if (meridiem.toUpperCase() === "AM" && hour === 12) hour = 0;
      return new Date(year, month - 1, day, hour, minute);
    }
    return null;
  };

  const formatTime = (dateString) => {
    const date = parseBackendDate(dateString);
    if (!date) return dateString || "N/A";

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return dateString;
  };

  // Get notification icon based on type
  const getNotificationIcon = (notification) => {
    const type = notification.notification_type || "";

    // Device notifications (Admin)
    if (type === 'device_login') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF4500" strokeWidth="2">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <circle cx="12" cy="16" r="1" />
          <path d="M8 8h8M8 12h6" />
          <circle cx="18" cy="8" r="2" fill="#FF4500" stroke="none" />
          <path d="M20 6l2-2M22 6l-2-2" stroke="#FF4500" />
        </svg>
      );
    }

    if (type === 'device_approved') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00B894" strokeWidth="2">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <circle cx="12" cy="16" r="1" />
          <path d="M8 8h8M8 12h6" />
          <circle cx="18" cy="8" r="2" fill="#00B894" stroke="none" />
          <path d="M17 7l2 2 4-4" stroke="#00B894" strokeWidth="2.5" />
        </svg>
      );
    }

    if (type === 'device_rejected') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF4757" strokeWidth="2">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <circle cx="12" cy="16" r="1" />
          <path d="M8 8h8M8 12h6" />
          <circle cx="18" cy="8" r="2" fill="#FF4757" stroke="none" />
          <path d="M20 6l-2 2M18 6l2 2" stroke="#FF4757" strokeWidth="2.5" />
        </svg>
      );
    }

    // Company notifications (SuperAdmin)
    if (type.startsWith("device")) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6C5CE7" strokeWidth="2">
          <rect x="4" y="4" width="16" height="12" rx="2" />
          <path d="M2 20h20" />
        </svg>
      );
    }

    if (type === "company_pending" || type === "company_registered") {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF9800" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
    }

    if (type === "company_approved") {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00B894" strokeWidth="2">
          <path d="M20 6L9 17L4 12" strokeWidth="2.5" />
        </svg>
      );
    }

    if (type === "company_rejected") {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF4757" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    }

    // Default
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4450A2" strokeWidth="2">
        <rect x="2" y="4" width="20" height="16" rx="2" />
      </svg>
    );
  };

  // Get notification title based on type
const getNotificationTitle = (notification) => {
  const title = notification.title || "New Notification";
  const type = notification.notification_type || "";

  let color = "#4450A2";

  // Approved
  if (type.includes("APPROVED") || type.includes("APPROVE")) {
    color = "#00B894";
  }

  // Rejected / Revoked / Withdrawn
  else if (
    type.includes("REJECTED") ||
    type.includes("REJECT") ||
    type.includes("REVOKED") ||
    type.includes("WITHDRAWN")
  ) {
    color = "#FF4757";
  }

  // Registration / Pending / Request
  else if (
    type.includes("PENDING") ||
    type.includes("REQUEST") ||
    type.includes("REGISTRATION")
  ) {
    color = "#FF9800";
  }

  // New device / login
  else if (
    type.includes("LOGIN") ||
    type.includes("NEW")
  ) {
    color = "#FF4500";
  }

  return (
    <span
      style={{
        color: color,
        fontWeight: "700",
      }}
    >
      {title}
    </span>
  );
};

  // Get status badge based on type
  const getStatusBadge = (notification) => {
    const type = notification.notification_type || "";

    // Device notifications (Admin)
    if (type === 'device_approved') {
      return <span className="notification-status-badge approved">✓ Approved</span>;
    }
    if (type === 'device_rejected') {
      return <span className="notification-status-badge rejected">✗ Rejected</span>;
    }
    if (type === 'device_login') {
      return <span className="notification-status-badge pending">⏳ Pending OTP</span>;
    }

    // Company notifications (SuperAdmin)
    if (type === "company_pending" || type === "company_registered") {
      return <span className="notification-status-badge pending">⏳ Pending</span>;
    }
    if (type === "company_approved") {
      return <span className="notification-status-badge approved">✓ Approved</span>;
    }
    if (type === "company_rejected") {
      return <span className="notification-status-badge rejected">✗ Rejected</span>;
    }

    return null;
  };

  // Get color based on notification type
  const getNotificationColor = (notification) => {
    const type = notification.notification_type || "";

    // Device notifications (Admin)
    if (type === 'device_approved') return '#00B894';
    if (type === 'device_rejected') return '#FF4757';
    if (type === 'device_login') return '#FF4500';

    // Company notifications (SuperAdmin)
    if (type.startsWith("device")) return '#6C5CE7';
    if (type === "company_pending" || type === "company_registered") return '#FF9800';
    if (type === "company_approved") return '#00B894';
    if (type === "company_rejected") return '#FF4757';

    return '#4450A2';
  };

  // Get background color based on notification type
  const getNotificationBgColor = (notification) => {
    const type = notification.notification_type || "";

    // Device notifications (Admin)
    if (type === 'device_approved') return 'rgba(0, 184, 148, 0.1)';
    if (type === 'device_rejected') return 'rgba(255, 71, 87, 0.1)';
    if (type === 'device_login') return 'rgba(255, 69, 0, 0.1)';

    // Company notifications (SuperAdmin)
    if (type.startsWith("device")) return 'rgba(108, 92, 231, 0.1)';
    if (type === "company_pending" || type === "company_registered") return 'rgba(255, 152, 0, 0.1)';
    if (type === "company_approved") return 'rgba(0, 184, 148, 0.1)';
    if (type === "company_rejected") return 'rgba(255, 71, 87, 0.1)';

    return 'rgba(68, 80, 162, 0.1)';
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="dashboard-content">
        <div className="notifications-page-header">
          <div className="notifications-header-left">
            <button className="back-to-dashboard-btn" onClick={() => navigate(dashboardPath)}>
              ← Back to Dashboard
            </button>
            <h1 className="notifications-page-title">All Notifications</h1>
          </div>
          <div className="notifications-header-right">
            <span className="notifications-total-count">
              {notifications.length} notifications
              {unreadCount > 0 && ` (${unreadCount} unread)`}
            </span>
          </div>
        </div>

        <div className="notifications-page-content">
          {isLoading ? (
            <div className="notifications-loading">Loading notifications...</div>
          ) : error ? (
            <div className="notifications-error">{error}</div>
          ) : notifications.length === 0 ? (
            <div className="notifications-empty">
              <span className="empty-icon">🔔</span>
              <h3>No notifications</h3>
              <p>You're all caught up!</p>
            </div>
          ) : (
            <div className="notifications-list-full">
              {notifications.map((notification) => {
                const color = getNotificationColor(notification);
                const bgColor = getNotificationBgColor(notification);
                
                return (
                  <div
                    key={notification.id}
                    className={`notification-item-full ${!notification.is_read ? 'unread' : ''}`}
                    style={{
                      borderLeft: !notification.is_read ? `4px solid ${color}` : 'none'
                    }}
                  >
                    <div className="notification-icon-full" style={{
                      background: !notification.is_read ? bgColor : 'rgba(68, 80, 162, 0.1)'
                    }}>
                      {getNotificationIcon(notification)}
                    </div>

                    <div className="notification-content-full">
                      <div className="notification-title-full">
                        {getNotificationTitle(notification)}
                      </div>

                     {role === "superadmin" && notification.company_name && (
  <div
    style={{
      fontSize: "13px",
      color: "#666",
      marginTop: "2px",
      fontWeight: "600",
    }}
  >
    {notification.company_name}
  </div>
)}

                      {notification.message && (
                        <div className="notification-message-full">
                          {notification.message}
                        </div>
                      )}

                      <div className="notification-details-full">
                        <div className="notification-meta-full">
                          <span className="notification-time-full">
                            {formatTime(notification.created_on)}
                          </span>
                          {getStatusBadge(notification)}
                          {!notification.is_read && (
                            <span className="notification-unread-badge">New</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;